import { useEffect, useState, type ReactNode } from "react";
import { holdBoot } from "../../i18n";
import { withChunkTimeout } from "../../../../../shared/lib/moduleLoadFailure";
import { CHUNK_WAIT_MS } from "../../lib/chunkWait";

/*
 * THE BOOKING FLOW'S STEP CHUNKS (rc.12 (a), Web Director). A booking route
 * loads the entry plus, only when reached, these named chunks (vite.config.ts
 * `NAMED_CHUNKS`; scripts/check-book-entry.js holds the list):
 *
 *   guest-code      the guest code step and change email (GuestCodeSteps.tsx)
 *   account-create  account creation (AccountCreate.tsx and what only it uses)
 *
 * Each is PREFETCHED where its step becomes reachable, for the people who can
 * reach it: guest-code when guest Review mounts (GuestReviewRoute);
 * account-create when the sign-in step mounts (BookSignIn), and at entry
 * evaluation for a first URL on /book/create (router/index.tsx).
 *
 * Not React.lazy: lazy suspends on its first render even when the module is
 * already here, so every prefetched step would still commit its fallback
 * first. `WithStepChunk` renders a loaded chunk in the same commit, and the
 * fallback only while a load is really on its way.
 *
 * A failed load is the route's error: thrown in render, RouteError asks
 * build-info, and shows the release notice only if a newer release is live,
 * the honest generic failure otherwise (docs/architecture/stale-build-guard.md).
 * A failed PREFETCH is silent and forgotten; the browser's module map may
 * still remember it, so the step's own load can fail the same way (rc.13 B).
 *
 * Focus is each screen's own, unchanged (c6-3): the fallback is a skeleton
 * whose heading takes no focus, and the step's heading moves focus (or not)
 * as it always did, when it mounts after its chunk.
 */
export interface StepChunk<T> {
  /** The module, once it is here. */
  loaded(): T | null;
  /** Load it (one load for every caller); a failure is forgotten. */
  load(): Promise<T>;
  /** Load it in the background; a failure is silent. */
  prefetch(): void;
}

export function stepChunk<T>(importer: () => Promise<T>): StepChunk<T> {
  let module: T | null = null;
  let pending: Promise<T> | null = null;
  const load = (): Promise<T> => {
    // Bounded (rc.13): a request that never settles becomes a load failure
    // after CHUNK_WAIT_MS, so no skeleton stays up forever.
    pending ??= withChunkTimeout(importer(), CHUNK_WAIT_MS).then(
      (loaded) => {
        module = loaded;
        return loaded;
      },
      (error: unknown) => {
        pending = null;
        throw error;
      }
    );
    return pending;
  };
  return {
    loaded: () => module,
    load,
    prefetch: () => {
      load().catch(() => {});
    },
  };
}

export const guestCodeChunk = stepChunk(() => import("./guest/GuestCodeSteps"));
export const accountCreateChunk = stepChunk(() => import("./AccountCreate"));
/**
 * The pay screens (views/Book/payScreens.ts), only where the payments surfaces
 * are built: null in a gate-closed build, where the literal keeps the import()
 * unscanned. Review prefetches it when it states a pay line.
 */
export const payScreensChunk = __LURA_PAYMENTS_UI__ ? stepChunk(() => import("./payScreens")) : null;
/** The sign-in step's form (entry headroom recovery, 2026-10-28): BookSignIn keeps the logic in the entry. */
export const signInChunk = stepChunk(() => import("./StepSignIn"));

/**
 * `children(module)` once the chunk is here, `fallback` until then. Rendering
 * this means the screen NEEDS the chunk, so a Spanish reader's boot waits for
 * it too (holdBoot): one skeleton, then the step.
 */
export function WithStepChunk<T>({
  chunk,
  fallback,
  failed,
  children,
}: {
  chunk: StepChunk<T>;
  fallback: ReactNode;
  /**
   * What a failed load shows IN PLACE, instead of throwing to the route's
   * error element. Only for a screen after a server answer (the pay screens,
   * W6 (b)): there the route's generic failure would be a wrong state, and a
   * retry could send again.
   */
  failed?: ReactNode;
  children: (module: T) => ReactNode;
}) {
  // KEYED BY CHUNK (the gate-on proofs, 2026-10-24): one WithStepChunk at one
  // place in the tree can be handed a DIFFERENT chunk on a later render (the
  // guest flow's code step, then its pay outcome), and React keeps the
  // instance. A module or failure kept for another chunk is never this one's:
  // destructuring PaidBookingOutcome from the code step's module rendered
  // `undefined` and threw.
  const [loaded, setLoaded] = useState<{ readonly chunk: StepChunk<T>; readonly module: T } | null>(() => {
    const ready = chunk.loaded();
    return ready ? { chunk, module: ready } : null;
  });
  const [failedFor, setFailure] = useState<{ readonly chunk: StepChunk<T>; readonly error: unknown } | null>(null);
  const module = loaded?.chunk === chunk ? loaded.module : chunk.loaded();
  const failure = failedFor?.chunk === chunk ? failedFor : null;
  useEffect(() => {
    if (module) return;
    let live = true;
    holdBoot(chunk.load()).then(
      (ready) => {
        if (live) setLoaded({ chunk, module: ready });
      },
      (error: unknown) => {
        if (live) setFailure({ chunk, error });
      }
    );
    return () => {
      live = false;
    };
  }, [chunk, module]);
  if (failure && failed !== undefined) return <>{failed}</>;
  // To the route's error element, which asks build-info (RouteError).
  if (failure) throw failure.error;
  return <>{module ? children(module) : fallback}</>;
}

/**
 * BEFORE A WRITE THAT LEADS TO A LAZY STEP, ITS CHUNK (rc.12 (a), the Web
 * Director; the same principle as rc.11: nothing is sent that the page cannot
 * follow through on). Asking for a guest code emails a code; if the code
 * step's chunk then failed, the code would be wasted. So the write awaits
 * `ready()` first: true once the chunk is here (at once for a prefetched
 * one), false when it is not, and then nothing is sent, and the failure is
 * thrown in render to the route's error element (which asks build-info).
 */
export function useChunkBeforeWrite<T>(chunk: StepChunk<T>): () => Promise<boolean> {
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  if (failure) throw failure.error;
  return () =>
    chunk.load().then(
      () => true,
      (error: unknown) => {
        setFailure({ error });
        return false;
      }
    );
}
